import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Inbox } from "lucide-react";
import { api } from "../lib/api";

/**
 * MyConciergeRequests — customer-dashboard widget that lists the customer's
 * own open/recent TREL Request Briefs (Phase 46). Each card surfaces the
 * brief request + an offer count, deep-linking to /customer/requests/:id.
 *
 * Kept compact (1-2 visible) so the widget is calm even on first launch.
 */
export default function MyConciergeRequests() {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/briefs/mine")
      .then((r) => alive && setBriefs(Array.isArray(r.data) ? r.data : []))
      .catch(() => alive && setBriefs([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading || briefs.length === 0) return null;

  return (
    <section
      data-testid="my-concierge-requests"
      className="rounded-3xl border border-[#00AEEF]/25 bg-gradient-to-br from-[#00AEEF]/6 via-white to-white p-5 sm:p-7"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-2xl bg-[#00AEEF]/10 text-[#00AEEF] flex items-center justify-center">
            <Inbox size={18}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#00AEEF]">My Concierge Requests</div>
            <h2 className="font-serif text-xl mt-0.5 text-[#071A2D] leading-tight">Offers waiting for you</h2>
          </div>
        </div>
        <Link to="/ai" className="text-[12px] font-medium text-[#FF7A45] hover:translate-y-px transition">+ New request</Link>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        {briefs.slice(0, 2).map((b) => (
          <Link
            key={b.brief_id}
            to={`/customer/requests/${b.brief_id}`}
            data-testid={`my-brief-${b.brief_id}`}
            className="block rounded-2xl border border-stone-200 bg-white p-4 hover:border-[#00AEEF]/40 hover:shadow-[0_4px_14px_-4px_rgba(0,174,239,0.18)] transition"
          >
            <div className="flex items-start gap-2">
              <Sparkles size={14} className="mt-0.5 text-[#C9A45C] shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-medium text-[#071A2D] truncate">{b.request}</div>
                <div className="text-[11.5px] text-stone-500 mt-1">
                  {b.city} · {b.guests || 2} {b.guests === 1 ? "guest" : "guests"} · <span className="font-semibold text-[#071A2D]">{b.offer_count} {b.offer_count === 1 ? "offer" : "offers"}</span>
                </div>
              </div>
              <ArrowRight size={14} className="mt-1 text-stone-400 shrink-0"/>
            </div>
          </Link>
        ))}
      </div>

      {briefs.length > 2 && (
        <div className="mt-4 text-[12px] text-stone-500">
          + {briefs.length - 2} more request{briefs.length - 2 === 1 ? "" : "s"} — open one to view offers.
        </div>
      )}
    </section>
  );
}
